/**
 * Client SSE dựng trên `fetch` + `ReadableStream` thay vì `EventSource`.
 *
 * `EventSource` không đặt được header, mà access token của ta đi trong
 * `Authorization`. Cách khác là nhét token vào query string — nhưng URL bị ghi
 * vào access log của proxy, vào history trình duyệt, và vào Referer. Token
 * trong log là token bị lộ.
 *
 * Đổi lại phải tự lo reconnect và tự bóc khung `data:` — chính là phần dưới đây.
 */

export interface StreamEvent {
  readonly event: string;
  readonly data: unknown;
  readonly id?: string;
}

export interface StreamOptions {
  readonly url: string;
  readonly getToken: () => string | null;
  readonly onEvent: (event: StreamEvent) => void;
  readonly signal: AbortSignal;
  /** Đổi được để test không phải chờ thật. */
  readonly retryDelays?: readonly number[];
}

/** Backoff mũ có trần. Proxy đóng idle connection là chuyện thường, đừng dội bom. */
const DEFAULT_RETRY_DELAYS = [1_000, 2_000, 5_000, 10_000, 30_000] as const;

export async function connectStream(options: StreamOptions): Promise<void> {
  const delays = options.retryDelays ?? DEFAULT_RETRY_DELAYS;
  let attempt = 0;
  let lastEventId: string | undefined;

  while (!options.signal.aborted) {
    try {
      const token = options.getToken();
      if (!token) throw new Error('chưa có access token');

      const response = await fetch(options.url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'text/event-stream',
          // Server dùng để gửi bù những gì client bỏ lỡ lúc mất kết nối
          // (docs/api-conventions.md §8).
          ...(lastEventId ? { 'Last-Event-ID': lastEventId } : {}),
        },
        signal: options.signal,
      });

      if (!response.ok || !response.body) throw new Error(`stream trả ${response.status}`);

      // Kết nối được thì reset backoff: lần rớt tiếp theo phải thử lại nhanh,
      // không kế thừa độ trễ của chuỗi lỗi trước đó.
      attempt = 0;

      for await (const event of readEvents(response.body, options.signal)) {
        if (event.id) lastEventId = event.id;
        options.onEvent(event);
      }
    } catch {
      // Nuốt lỗi có chủ đích: mọi kiểu rớt kết nối đều dẫn tới cùng một hành
      // động là thử lại. Abort thì vòng lặp thoát ở điều kiện `while`.
    }

    if (options.signal.aborted) return;

    const delay = delays[Math.min(attempt, delays.length - 1)] ?? 30_000;
    attempt += 1;
    await sleep(delay, options.signal);
  }
}

async function* readEvents(
  body: ReadableStream<Uint8Array>,
  signal: AbortSignal,
): AsyncGenerator<StreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (!signal.aborted) {
      const { done, value } = await reader.read();
      if (done) return;

      buffer += decoder.decode(value, { stream: true });

      // Khung SSE kết thúc bằng một dòng trống. Gói tin TCP có thể cắt giữa
      // chừng, nên phần dư phải ở lại buffer chờ mảnh tiếp theo.
      let boundary = buffer.indexOf('\n\n');
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);

        const parsed = parseFrame(frame);
        if (parsed) yield parsed;

        boundary = buffer.indexOf('\n\n');
      }
    }
  } finally {
    // Không `cancel()` thì connection bị giữ lại và trình duyệt cạn slot HTTP
    // sau vài lần điều hướng.
    await reader.cancel().catch(() => undefined);
  }
}

function parseFrame(frame: string): StreamEvent | null {
  let event = 'message';
  let id: string | undefined;
  const dataLines: string[] = [];

  for (const line of frame.split('\n')) {
    if (line.startsWith(':')) continue; // comment / keep-alive
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    const value = colon === -1 ? '' : line.slice(colon + 1).trimStart();

    if (field === 'event') event = value;
    else if (field === 'id') id = value;
    else if (field === 'data') dataLines.push(value);
  }

  if (dataLines.length === 0) return null;

  const raw = dataLines.join('\n');
  try {
    return { event, data: JSON.parse(raw) as unknown, ...(id ? { id } : {}) };
  } catch {
    return { event, data: raw, ...(id ? { id } : {}) };
  }
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {
      clearTimeout(timer);
      resolve();
    });
  });
}
